import { getSegmentTitle } from "./segmentLabels";

/**
 * mergeSegmentWithSettings.js — the ONE upgrade-only merge of a segment with
 * its `settings` sub-object, shared by every render surface.
 *
 * WHY THIS EXISTS (stg-0920-old-wheels-null-segment-settings-overwrite-colour-
 * and-title-grey-ring-P0, MEASURED live on web-dev.iyuga.net wheel 6705 /
 * segment 45031, crafted to match staging wheel 270's real shape): every call
 * site that renders a segment merges it with `{ ...segment, ...segment.settings }`.
 * A JS object spread copies an explicit `null` just like any other value, so a
 * `settings` row created with `color: null` / `title: null` (an OLD-SHAPE
 * wheel whose colour/title still live only at the segment's top level) blanks
 * the segment's perfectly good top-level `color`/`title` instead of leaving it
 * alone. Downstream, the wheel renderer paints `d.data.color || DEFAULT_WEDGE_COLOR`
 * (grey) and only draws a rim pill when `segmentLabelText(d.data.title) !== ""`
 * (title null -> "" -> no pill) — so the wedge goes grey and the label vanishes
 * for data that is otherwise completely fine.
 *
 * `settings` is still the AUTHORITY when it actually carries a value (a real
 * edit in Segment Settings must still win over the top-level field) — this
 * merge only refuses to let an explicit null/undefined settings value blank an
 * existing top-level one. Every consumer that used to spread `...segment.settings`
 * unconditionally should use this instead so the law can never re-drift between
 * surfaces.
 *
 * `title` gets one further reconciliation, per
 * gordon-0924-editwheel-builder-open-blanks-segment-titles-settings-title-
 * shadow (dev #6131 / stgport #6366): a segment created via the builder's
 * "Add segment" pill can carry a populated top-level `title` alongside a
 * `settings.title` of `""` (an empty string, not null/undefined, so the
 * generic rule above would otherwise let it blank the title on every render
 * surface that shares this merge — Edit Wheel's
 * own segment rail AND the public ViewWheel rim labels). Delegate to
 * segmentLabels.getSegmentTitle, the same "prefer a non-empty settings.title,
 * else the top-level title" rule already used by the computed-label layer, so
 * `title` alone also treats "" as absent while every other settings key keeps
 * the null/undefined-only rule above.
 *
 * Pure: no React, no redux, no DOM, no I/O. Does not mutate its input.
 *
 * @param {object|null|undefined} segment a `wheel_segments` entry
 * @returns {object} a NEW object: `segment`'s own fields, with every
 *   non-null/undefined `segment.settings` field applied on top
 */
export const mergeSegmentWithSettings = (segment) => {
  const settings = (segment && segment.settings) || {};
  const merged = { ...segment };
  Object.keys(settings).forEach((key) => {
    // stgqa-0927-P0-kelly-gate-d-locks (r100519): `settings.id` is a
    // different database row (SegmentSettings) from `segment.id` (the
    // Activity/segment itself) — letting it win here silently swaps the
    // segment's real id for an unrelated row id, corrupting every downstream
    // consumer (POST /wheel/activity/<id>/unlock/ 404s using settings' id).
    if (key === "id") return;
    const value = settings[key];
    if (value !== null && value !== undefined) {
      merged[key] = value;
    }
  });
  if (segment) {
    merged.title = getSegmentTitle(segment);
  }
  return merged;
};

export default mergeSegmentWithSettings;
